import { useState, useEffect, useRef, useCallback } from 'react';
import type { UseTwilioDeviceReturn, TwilioCallInfo } from './use-twilio-device';

// FIX 3: Corrected @signalwire/js SDK v4 API usage:
// - SignalWire is a class — use `new SignalWire(credentialProvider)`
// - Connect via client.connect() then client.register() for inbound
// - client.isConnected$ is an Observable<boolean> for connection status
// - client.dial(destination) returns Promise<Call>
// - call.status$ is Observable<CallStatus> — subscribe for state changes
// - Mute via call.self?.mute() / call.self?.unmute() (SelfParticipant)
// - call.sendDigits(digits) for DTMF

export function useSignalWireDevice(userId?: string): UseTwilioDeviceReturn {
  const clientRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  const subscriptionsRef = useRef<Array<{ unsubscribe: () => void }>>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [callState, setCallState] = useState<'idle' | 'connecting' | 'ringing' | 'connected' | 'disconnected'>('idle');
  const [activeCallInfo, setActiveCallInfo] = useState<TwilioCallInfo | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    setCallDuration(0);
    timerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
  }, [stopTimer]);

  const clearSubscriptions = useCallback(() => {
    subscriptionsRef.current.forEach(sub => sub.unsubscribe());
    subscriptionsRef.current = [];
  }, []);

  const cleanupCall = useCallback(() => {
    clearSubscriptions();
    callRef.current = null;
    setCallState('idle');
    setActiveCallInfo(null);
    setIsMuted(false);
    stopTimer();
  }, [stopTimer, clearSubscriptions]);

  const initDevice = useCallback(async () => {
    if (!userId) return;
    try {
      setError(null);
      const tokenRes = await fetch('/api/calls/token', { credentials: 'include' });
      if (!tokenRes.ok) {
        if (tokenRes.status === 401) console.log('[SignalWireDevice] Not authenticated yet');
        else console.warn('[SignalWireDevice] Token request failed:', tokenRes.status);
        return;
      }
      const { token } = await tokenRes.json();
      if (!token) { setError('Failed to obtain voice token'); return; }

      if (clientRef.current) {
        // Token refresh — SATs are not refreshable in-place; re-init with new client
        await clientRef.current.disconnect?.();
        clientRef.current = null;
      }

      // Dynamic import to avoid SSR issues with the browser SDK
      const { SignalWire, StaticCredentialProvider } = await import('@signalwire/js');

      // FIX 3: use `new SignalWire(...)` — it is a class, not a callable function
      const client = new SignalWire(new StaticCredentialProvider({ token }));

      // Subscribe to connection state via isConnected$ observable
      const connSub = client.isConnected$.subscribe((connected: boolean) => {
        if (connected) {
          setIsReady(true);
          setError(null);
        } else {
          setIsReady(false);
        }
      });
      subscriptionsRef.current.push(connSub);

      // Subscribe to errors$ observable
      const errSub = client.errors$.subscribe((err: any) => {
        setError(err?.message || 'SignalWire device error');
      });
      subscriptionsRef.current.push(errSub);

      // FIX 3: use connect() then register() — not online()
      await client.connect();
      await client.register();

      clientRef.current = client;
    } catch (err) {
      console.warn('[SignalWireDevice] Init failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize calling');
    }
  }, [userId, clearSubscriptions]);

  useEffect(() => {
    initDevice();
    return () => {
      clearSubscriptions();
      clientRef.current?.disconnect?.();
      clientRef.current = null;
      stopTimer();
    };
  }, [initDevice, stopTimer, clearSubscriptions]);

  const makeCall = useCallback((params: TwilioCallInfo) => {
    if (!clientRef.current || !isReady) { setError('Phone service not ready.'); return; }
    if (callRef.current) { setError('A call is already in progress.'); return; }

    setCallState('connecting');
    setActiveCallInfo(params);
    setError(null);

    // FIX 3: client.dial(destination) — takes a string destination, not {to: ...}
    clientRef.current.dial(params.to).then((call: any) => {
      callRef.current = call;

      // FIX 3: subscribe to call.status$ observable for call state changes
      const statusSub = call.status$.subscribe((status: string) => {
        switch (status) {
          case 'ringing':
            setCallState('ringing');
            break;
          case 'connected':
            setCallState('connected');
            startTimer();
            break;
          case 'disconnected':
          case 'failed':
          case 'destroyed':
            setCallState('disconnected');
            cleanupCall();
            break;
        }
      });
      subscriptionsRef.current.push(statusSub);
    }).catch((err: Error) => {
      setError(err.message || 'Failed to connect call');
      setCallState('idle');
      setActiveCallInfo(null);
    });
  }, [isReady, startTimer, cleanupCall]);

  const hangUp = useCallback(() => {
    callRef.current?.hangup?.();
    cleanupCall();
  }, [cleanupCall]);

  const toggleMute = useCallback(async () => {
    if (callRef.current) {
      const newMuted = !isMuted;
      try {
        // FIX 3: use call.self.mute() / call.self.unmute() — not call.setMuted(bool)
        if (newMuted) {
          await callRef.current.self?.mute?.();
        } else {
          await callRef.current.self?.unmute?.();
        }
        setIsMuted(newMuted);
      } catch (err) {
        console.warn('[SignalWireDevice] Mute toggle failed:', err);
      }
    }
  }, [isMuted]);

  const sendDigits = useCallback((digits: string) => {
    callRef.current?.sendDigits?.(digits);
  }, []);

  return { isReady, callState, activeCallInfo, callDuration, isMuted, error, makeCall, hangUp, toggleMute, sendDigits };
}
